import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { eventById, searchEvents, upcoming } from '../data/events';
import { store, useCatalog } from '../data/store';
import { fromSlimRow } from '../data/to-event';
import { trackSearch, trackSearchTap } from './track';
import { settledSearch, tappedSearch } from '../../shared/search-stat.mjs';
import type { Event } from '../types';

/*
 * Search in the browser: /api/search (D1 full-text, functions/api/search.ts) with the local
 * scan of src/data/events.ts as the fallback. The local scorer strips every digit from the
 * query and matches with `haystack.includes(token)` ("49ers" → "ers": Rangers, Packers…), so it
 * only stands in when the API cannot answer.
 */

type Row = {
  id: string;
  slug?: string;
  name: string;
  artist?: string | null;
  teams?: string[] | null;
  date?: string;
  event_date?: string;
  time?: string | null;
  venue: string;
  city: string;
  image?: string | null;
  price?: number | null;
  allIn?: boolean;
  category: string;
  kind?: string | null;
  genre?: string | null;
  lat?: number | null;
  lng?: number | null;
  country?: string | null;
  demand?: number | null;
  /** 'soon': announced, not on our partner marketplaces yet */
  status?: string | null;
};

/** A search row → an Event the cards can draw (with its time, place and country, so the
 *  search page's showtime and distance filters work on it). Full rows already in the store
 *  win; otherwise a slim one is added so later pages can reuse it. */
const rowToEvent = (r: Row): Event => {
  const have = eventById(r.id);
  if (have && !have.partial) return have;
  const e = fromSlimRow([
    r.id, r.name, r.artist ?? null, r.teams ?? null, r.date ?? r.event_date ?? '', r.time ?? null, r.venue, r.city, r.category,
    r.kind ?? null, r.genre ?? null, r.price ?? null, r.allIn ? 1 : 0, r.image ?? null, null, r.lat ?? null, r.lng ?? null, null,
    r.country ?? null, r.demand ?? null,
  ]);
  store.add([e]);
  return e;
};

/** A list page the query names (functions/api/search.ts). Genre and category pages carry their
 *  own address (`href`), and so does a tournament ('tour': the World Series' page,
 *  /price-index/world-series); `label` tells same-name pages apart ("NFL", "Concerts · Sphere"). */
export type HubHit = {
  kind: 'artist' | 'team' | 'venue' | 'city' | 'league' | 'genre' | 'cat' | 'tour';
  slug: string;
  name: string;
  n: number;
  min: number | null;
  next: string | null;
  exact: boolean;
  image: string | null;
  href?: string;
  label?: { tag: string | null; where: string | null } | null;
};
export const hubHref = (h: HubHit) => h.href ?? `/${h.kind}/${h.slug}`;
/** The API's key for a page ("genre:music:jazz-and-blues"), as `which` lists them. */
export const hubKey = (h: HubHit) => (h.kind === 'genre' && h.href ? `genre:${h.href.split('/').filter(Boolean).join(':')}` : `${h.kind}:${h.slug}`);
/** A page kind in the reader's language (literal t() calls, so the dictionaries check them). */
export const hubKindLabel = (t: (k: string) => string, kind: HubHit['kind']) =>
  kind === 'artist'
    ? t('Artist')
    : kind === 'team'
      ? t('Team')
      : kind === 'venue'
        ? t('Venue')
        : kind === 'city'
          ? t('City')
          : kind === 'league'
            ? t('League')
            : kind === 'genre'
              ? t('Genre')
              : kind === 'tour'
                ? t('Tournament')
                : t('Category');

/** What the search API says besides the rows (functions/api/search.ts SearchBody). */
export type SearchMeta = {
  /** two or three pages the query names equally: keys `${kind}:${slug}` */
  which: string[];
  /** other matches, below the best page's own dates or a head-to-head's games */
  more: Event[];
  notice?: 'tribute' | 'no-dates' | 'none' | 'partial' | 'postseason' | 'help' | 'elsewhere';
  noticeName?: string;
  /** a partial answer of tribute and cover shows only (noticeName: the act) */
  tribute?: boolean;
  dropped?: string;
  didYouMean?: string;
  original?: string;
  applied?: boolean;
  reading?: {
    cat?: string;
    /** a sport the words named ("voetbal"): soccer */
    sport?: string;
    /** the holiday a time word named */
    holiday?: string;
    /** the date of a weekday a time word named ("saturday") */
    day?: string;
    genre?: { name: string; href: string };
    when?: string;
    month?: string;
    near?: boolean;
    place?: { name: string; slug: string };
    entity?: { name: string; kind: string; slug: string };
  };
  route?: { href: string; label: string };
  order?: 'date' | 'near' | 'match';
  /** the API stopped at its limit (counted before duplicate shows were merged) */
  capped?: boolean;
};
const NO_META: SearchMeta = { which: [], more: [] };

const norm = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
/**
 * One row per real show: two partners listing the same act on the same day at the same time
 * are one show ("The Kia Forum, Los Angeles" and "Kia Forum, Inglewood"; an act cannot play
 * two venues at once). The priced row with the lowest price stays.
 */
export const dedupeShows = (events: Event[]): Event[] => {
  const best = new Map<string, Event>();
  for (const e of events) {
    const key = `${norm(e.artist ?? e.name)}|${e.date}|${e.time ?? ''}`;
    const have = best.get(key);
    const p = (x: Event) => (typeof x.lowestPrice === 'number' && x.lowestPrice > 0 ? x.lowestPrice : Infinity);
    if (!have || p(e) < p(have)) best.set(key, e);
  }
  const keep = new Set(best.values());
  return events.filter((e) => keep.has(e));
};
const byDate = (a: Event, b: Event) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? '');

/** `total`/`next`: the page cut of the full answer (functions/api/search.ts pageOf); later pages carry only rows. */
type Body = { events: Row[]; hubs: HubHit[]; more?: Row[]; which?: string[]; total?: number; next?: number } & Omit<SearchMeta, 'which' | 'more'>;
type Answer = { key: string; q: string; rows: Row[]; events: Event[]; hubs: HubHit[]; meta: SearchMeta; soon: Set<string>; total: number; next: number | null };
export type SearchState = {
  events: Event[];
  hubs: HubHit[];
  meta: SearchMeta;
  /** ids of rows announced but not on our partner marketplaces yet ("not listed yet") */
  soon: Set<string>;
  /** no answer for THIS query yet (what shows may be the previous query's answer) */
  pending: boolean;
  /** an answer has arrived since the box was opened: no skeleton from here on */
  answered: boolean;
  /** the query whose API answer is on screen (the previous query's while `pending`); null when
   *  nothing is, or the local scan answered — the settled-search counter counts only the API's */
  answerFor: string | null;
  /** rows the answer holds in all (the API's count, before duplicate shows merge), else what is loaded */
  total: number;
  /** the next page is not loaded yet: `loadMore` fetches it */
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
};

/** One request shape for the type-ahead (the overlay, the homepage box) and the search page: v=2 is
 *  this bundle (genre and category pages' addresses, the fields of SearchMeta). A SUBMITTED query
 *  adds `&s=1` (the page asks settled: the guards on stop words, short names and aliases apply),
 *  so it is a request of its own — the answer the box typed ahead is not reused. */
export const SEARCH_LIMIT = 100;
/** A page of the answer: `n` rows from offset `o` (the pages and notices come with the first page). Without `n`, the whole answer. */
export const searchUrl = (q: string, exact = false, settled = false, n = 0, o = 0) =>
  `/api/search?q=${encodeURIComponent(q)}&${n ? `n=${n}${o ? `&o=${o}` : ''}` : `limit=${SEARCH_LIMIT}`}&hubs=1&v=2${exact ? '&exact=1' : ''}${settled ? '&s=1' : ''}`;
/** How many rows a search page and a search box ask for first. */
export const SEARCH_PAGE = 9;
export const BOX_PAGE = 12;
/** No answer this long: the local scan stands in (it downloads the 1.3 MB index once). */
const SLOW_MS = 2500;

/**
 * The search page and the search box: events (one row per show) plus the artist / team / venue
 * / city / genre pages the query names, best first, and what the API says about them (a
 * spelling it answered instead, a "which one?", tribute shows, an intent reading). Date order,
 * except where the order IS the answer: nearest first, or partial matches. `exact`: no spelling
 * correction.
 *
 * Feels instant without guessing: the previous answer stays on screen while the next query's
 * is on its way (the list never blanks between keystrokes, nothing is inserted above painted
 * rows), a superseded request is cancelled, and the local index — 1.3 MB — is fetched only
 * when the API fails or takes over 2.5 s; an API answer for the same query always wins.
 */
export const useSearch = (query: string, opts: { exact?: boolean; settled?: boolean; page?: number; all?: boolean } = {}): SearchState => {
  const q = query.trim();
  const exact = !!opts.exact;
  const settled = !!opts.settled;
  // rows asked for first: `page` (a search box's 12, the search page's 9), or the whole answer (`all`: a filter or
  // a sort the page applies over every row); the rest comes by `loadMore`
  const n = opts.all ? SEARCH_LIMIT : (opts.page ?? BOX_PAGE);
  const key = `${q}|${exact ? 1 : 0}${settled ? 's' : ''}|${n}`;
  const [remote, setRemote] = useState<Answer | null>(null);
  const [fallback, setFallback] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const remoteRef = useRef<Answer | null>(null);
  remoteRef.current = remote;
  const moreCtrl = useRef<AbortController | null>(null);

  useEffect(() => {
    if (q.length < 2) return;
    const ctrl = new AbortController();
    const slow = setTimeout(() => setFallback(key), SLOW_MS);
    // Debounced: one request per pause in typing, not one per keystroke.
    const t = setTimeout(() => {
      fetch(searchUrl(q, exact, settled, n), { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((body: Body) => {
          const { events: _e, hubs: _h, more: _m, which, total: _t, next, ...rest } = body;
          clearTimeout(slow);
          setRemote(build(key, q, body.events, body.hubs ?? [], { ...rest, which: which ?? [], more: [] }, body.more ?? [], body.total ?? body.events.length, next ?? null));
        })
        .catch((err) => {
          if (ctrl.signal.aborted || (err as Error)?.name === 'AbortError') return;
          setFallback(key);
        });
    }, 160);
    return () => {
      ctrl.abort();
      clearTimeout(t);
      clearTimeout(slow);
    };
  }, [q, exact, settled, key, n]);

  // the next page of the answer on screen (a scroll to the end of the list, or its "Show more")
  const loadMore = useCallback(() => {
    const cur = remoteRef.current;
    if (!cur || cur.key !== key || cur.next === null || moreCtrl.current) return;
    const ctrl = new AbortController();
    moreCtrl.current = ctrl;
    setLoadingMore(true);
    fetch(searchUrl(cur.q, exact, settled, n, cur.next), { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body: Body) => {
        setRemote((now) => (!now || now.key !== cur.key || now.next !== cur.next ? now : build(now.key, now.q, [...now.rows, ...body.events], now.hubs, now.meta, body.more ?? [], now.total, body.next ?? null)));
      })
      .catch(() => undefined)
      .finally(() => {
        if (moreCtrl.current === ctrl) moreCtrl.current = null;
        setLoadingMore(false);
      });
  }, [key, exact, settled, n]);
  // another query: a page still on its way is no longer wanted
  useEffect(
    () => () => {
      moreCtrl.current?.abort();
      moreCtrl.current = null;
      setLoadingMore(false);
    },
    [key]
  );

  // the local scan, only for a query the API failed or kept waiting
  const useLocal = q.length >= 2 && fallback === key && remote?.key !== key;
  const { version } = useCatalog(useLocal ? ['index'] : []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const local = useMemo(() => (useLocal ? dedupeShows([...searchEvents(q, upcoming())].sort(byDate)) : null), [useLocal, q, version]);

  const idle = { total: 0, hasMore: false, loadingMore: false, loadMore };
  if (q.length < 2) return { events: NO_EVENTS, hubs: NO_HUBS, meta: NO_META, soon: NO_SOON, pending: false, answered: !!remote, answerFor: null, ...idle };
  if (remote?.key === key)
    return { events: remote.events, hubs: remote.hubs, meta: remote.meta, soon: remote.soon, pending: false, answered: true, answerFor: remote.q, total: remote.total, hasMore: remote.next !== null, loadingMore, loadMore };
  if (local) return { events: local, hubs: NO_HUBS, meta: NO_META, soon: NO_SOON, pending: false, answered: true, answerFor: null, ...idle, total: local.length };
  // on its way: the previous answer stays (never a blank list between keystrokes)
  if (remote) return { events: remote.events, hubs: remote.hubs, meta: remote.meta, soon: remote.soon, pending: true, answered: true, answerFor: remote.q, ...idle, total: remote.total };
  return { events: NO_EVENTS, hubs: NO_HUBS, meta: NO_META, soon: NO_SOON, pending: true, answered: false, answerFor: null, ...idle };
};

/** The rows of an answer (the pages loaded so far) as the cards draw them: nearest first and partial matches keep the
 *  server's order, everything else by date; one row per show. A later page only adds rows (and the `more` list). */
const build = (key: string, q: string, rows: Row[], hubs: HubHit[], meta: SearchMeta, moreRows: Row[], total: number, next: number | null): Answer => {
  const keepOrder = meta.order === 'near' || meta.notice === 'partial';
  const mapped = rows.map(rowToEvent);
  const events = dedupeShows(keepOrder ? mapped : mapped.sort(byDate));
  const shown = new Set(events.map((e) => e.id));
  const moreEvents = moreRows.length ? dedupeShows(moreRows.map(rowToEvent).filter((e) => !shown.has(e.id))) : meta.more;
  const soon = new Set([...rows, ...moreRows].filter((r) => r.status === 'soon').map((r) => r.id));
  return { key, q, rows, events, hubs, meta: { ...meta, more: moreEvents }, soon, total, next };
};
const NO_SOON = new Set<string>();
const NO_EVENTS: Event[] = [];
const NO_HUBS: HubHit[] = [];

/** How long a search box's answer stands still before its search counts: the visitor stopped typing. */
export const SEARCH_SETTLE_MS = 1000;

/**
 * The settled-search counter (src/lib/track.ts; its rules in shared/search-stat.mjs) for a search
 * box or the search page. A search counts once the API's answer for it has stood `settleMs`: a box
 * passes SEARCH_SETTLE_MS, the search page 0 (its query was submitted). Never the local scan, never
 * a prefix the visitor typed past (its timer is cancelled), once per query per page load.
 *
 * Returns the tap counter for a result (`hub`: the page tapped, if it is one). The tap belongs to
 * the answer on screen, and counts that search first if it had not settled yet (a tap means it had).
 */
export const useSearchCount = (s: SearchState, settleMs: number) => {
  const { answerFor, pending, events, hubs, meta, total, hasMore } = s;
  useEffect(() => {
    const c = settledSearch(searchView({ answerFor, pending, events, hubs, meta, total, hasMore }));
    if (!c) return;
    const t = setTimeout(() => trackSearch(c.q, c.n, c.hub, settleMs === 0), settleMs);
    return () => clearTimeout(t);
  }, [answerFor, pending, events, hubs, meta, total, hasMore, settleMs]);
  return (hub?: HubHit | null) => {
    const c = tappedSearch(searchView(s));
    if (!c) return;
    trackSearch(c.q, c.n, c.hub, settleMs === 0);
    trackSearchTap(c.q, hub ? hubKey(hub) : null);
  };
};
/** A search on screen in the counter's terms (shared/search-stat.mjs SearchView). */
const searchView = (s: Pick<SearchState, 'answerFor' | 'pending' | 'events' | 'hubs' | 'meta' | 'total' | 'hasMore'>) => ({
  answerFor: s.answerFor,
  pending: s.pending,
  // (a page not loaded yet still counts: the answer found its `total` rows)
  events: s.hasMore ? Math.max(s.events.length, s.total) : s.events.length,
  more: s.meta.more.length,
  hubs: s.hubs.map(hubKey),
  notice: s.meta.notice ?? null,
  entity: s.meta.reading?.entity ? `${s.meta.reading.entity.kind}:${s.meta.reading.entity.slug}` : null,
});
